import * as React from 'react';

import {
  Box,
  Button,
  Stack,
} from '@mui/material';

import { LoadingButton } from '@mui/lab';

import SubmitIcon from '@mui/icons-material/Publish';
import ApproveIcon from '@mui/icons-material/DoneAll';

import { FormProvider, useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';

import { useSnackbar } from 'notistack';

import { yupResolver } from '@hookform/resolvers/yup';
import ControlledForm from '../../components/ControlledForm';
import ConfirmationInputDialog from '../../components/ConfirmationInputDialog';
import ScrollToTop from '../../components/ScrollToTop';

import FormReadOnlySection from '../../components/FormReadOnlySection';
import FormPaper from '../../components/FormPaper';
import FormTitle from '../../components/FormTitle';
import FormHeader from '../../components/FormHeader';

import { useAuthorizedFormData } from '../../core/context/AuthorizedFormDataContext';
import { useCabinEventReportData } from '../../core/context/CabinEventReportDataContext';
import useDeleteCabinEventDraft from '../../core/api/hooks/cabin-event/useDeleteCabinEventDraft';
import { EventError, CabinEventReport, FlightEventReport } from '../../core/api/clients';

import FlightStep from './FormSteps/FlightStep';
import EventStep from './FormSteps/EventStep';
import NarrativeStep from './FormSteps/NarrativeStep';

import '../../core/styles/disabledFields.css';
import FormStatus from '../../components/FormStatus';
import { FormValidationProvider } from '../../core/context/FormValidationContext';
import queryKeys from '../../core/api/queryKeys';
import { useGlobalFormState } from '../../core/context/GlobalFormStateContext';
import CabinEventFormValidationSchema from './CabinEventFormValidationSchema';

function CabinEventFormView() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const authorizedFormData = useAuthorizedFormData();
  const { enqueueSnackbar } = useSnackbar();

  const {
    cabinEventReport,
    update,
    approve,
    submit,
    allowedActions,
    submitStatus,
    approveStatus,
  } = useCabinEventReportData();
  const { mutate: deleteCabinEventDraft } = useDeleteCabinEventDraft();
  const [confDialogOpen, setConfDialogOpen] = React.useState<boolean>(false);

  const formMethods = useForm<CabinEventReport>({
    defaultValues: cabinEventReport,
    mode: 'onBlur',
    resolver: yupResolver(CabinEventFormValidationSchema),
  });

  React.useEffect(() => {
    formMethods.reset(cabinEventReport);
  }, [cabinEventReport]);

  const submitCallback = () => {
    Promise.all([
      queryClient.refetchQueries({ queryKey: [queryKeys.CabinEvent.Drafts] }),
      queryClient.refetchQueries({ queryKey: [queryKeys.CabinEvent.Submissions] }),
    ]).then(() => {
      navigate('/');
    });
  };

  const handleUpdate = () => {
    update(formMethods.getValues());
  };

  const handleSubmit = () => {
    submit(cabinEventReport.id, submitCallback);
  };

  const approveCallback = (eventReport: FlightEventReport) => {
    // setting timeout for navigating allows refetch to finish
    // and display updated data in the approval list
    queryClient
      .refetchQueries({ queryKey: [queryKeys.CabinEvent.ApprovalList] })
      .then(() => {
        navigate('/form/cabin-event', {
          state: {
            isError: false,
            title: 'The report was successfully approved',
            message: `Occurrence number is ${eventReport.documentNumber}`,
          },
        });
      });
  };

  const approveFailCallback = (error: EventError) => enqueueSnackbar(error.message, { variant: 'error' });

  const handleApprove = () => {
    approve(cabinEventReport.id, approveCallback, approveFailCallback);
  };

  const handleDelete = (reason:string) => {
    if (cabinEventReport.id) {
      const request = { id: cabinEventReport.id, reason };
      deleteCabinEventDraft(request, {
        onSuccess: () => {
          queryClient
            .refetchQueries({ queryKey: [queryKeys.CabinEvent.ApprovalList] })
            .then(() => {
              navigate('/form/cabin-event');
            });
        },
        onError: () => enqueueSnackbar('Error deleting form', { variant: 'error' }),
      });
    }
  };

  const openDeleteConfirmationDialog = () => {
    setConfDialogOpen(true);
  };

  return (
    <FormPaper color={authorizedFormData.metadata?.color}>
      <ScrollToTop />
      <ConfirmationInputDialog
        title="Confirm Delete"
        dialogText="Are you sure you want to delete this form?"
        inputLengthRequirement={5}
        isOpen={confDialogOpen}
        setOpen={setConfDialogOpen}
        confirm={handleDelete}
        actionButtonText="Delete"
      />
      <FormHeader>
        <FormTitle
          title="Cabin Event Report"
          subtitle={[[
            'Reported By:',
            cabinEventReport.employeeName,
            `[${cabinEventReport.employeeId}],`,
            cabinEventReport.employeeLocation,
            cabinEventReport.employeeTitle,
          ].join(' ')]}
        />
        <FormStatus
          status={cabinEventReport.status}
          subtext={cabinEventReport.reasonDeleted}
        />
      </FormHeader>
      <ControlledForm onUpdate={handleUpdate} enableUpdate>
        <FormProvider {...formMethods}>
          <FormValidationProvider<CabinEventReport>>
            <Stack spacing={2}>
              <FormReadOnlySection title="Flight & Crew">
                <FlightStep visible readOnly />
              </FormReadOnlySection>
              <FormReadOnlySection title="Event">
                <EventStep visible readOnly />
              </FormReadOnlySection>
              <FormReadOnlySection title="Narrative">
                <NarrativeStep visible readOnly />
              </FormReadOnlySection>
            </Stack>
          </FormValidationProvider>
        </FormProvider>
      </ControlledForm>
      <Box display="flex" sx={{ my: 2 }}>
        <Button
          sx={{ marginRight: 2 }}
          onClick={() => navigate('/form/cabin-event')}
        >
          Close
        </Button>
        {allowedActions?.delete && (
          <Button
            sx={{ marginRight: 2 }}
            color="error"
            variant="outlined"
            onClick={openDeleteConfirmationDialog}
          >
            Delete
          </Button>
        )}
        <Box flexGrow={1} />
        {allowedActions?.submit && (
          <LoadingButton
            loading={submitStatus === 'pending'}
            loadingPosition="end"
            endIcon={<SubmitIcon />}
            sx={{ marginRight: 2 }}
            color="primary"
            variant="contained"
            onClick={handleSubmit}
            disabled={formMethods.formState.isDirty}
          >
            {`${formMethods.formState.isDirty ? 'Submit (Unsaved)' : 'Submit'}`}
          </LoadingButton>
        )}
        {allowedActions?.edit && (
          <Button
            sx={{ marginRight: 2 }}
            color="primary"
            variant="outlined"
            onClick={() => navigate(`/form/cabin-event/${cabinEventReport.id}/edit`)}
          >
            Edit
          </Button>
        )}
        {allowedActions?.approve && (
          <LoadingButton
            loading={approveStatus === 'pending'}
            loadingPosition="end"
            endIcon={<ApproveIcon />}
            sx={{ marginRight: 2 }}
            color="primary"
            variant="contained"
            onClick={handleApprove}
            disabled={formMethods.formState.isDirty}
          >
            {`${formMethods.formState.isDirty ? 'Approve (Unsaved)' : 'Approve'}`}
          </LoadingButton>
        )}
      </Box>
    </FormPaper>
  );
}

export default CabinEventFormView;
